<main class="dashboard-page container-fluid py-3" aria-label="Dashboard">
  <app-chat-container></app-chat-container>
  @if (isLoading) {
    <app-dashboard-skeleton></app-dashboard-skeleton>
  } @else {
  <div class="px-3 px-md-4">
    @if (errorMessage) {
      <div class="w-100 h-100 d-flex justify-content-center align-items-center dashboard-error-container">
        <div class="alert alert-danger" role="alert">
          {{ errorMessage }}
        </div>
      </div>
    }
    @else {
      <div class="dashboard-header mb-3 text-center">
        <h2 class="text-muted small mb-0">Application Modernization AI Assessment Summary</h2>
      </div>

      <!-- Global Application Search -->
      <div class="row justify-content-center mb-4">
        <div class="col-12 col-md-8 col-lg-6">
          <app-global-search></app-global-search>
        </div>
      </div>

      <!-- Strategy Distribution & AI Code Assessment -->
      <section aria-label="Dashboard charts" class="charts-section mb-3">
        <div class="row g-3">
          <!-- Strategy Distribution -->
          <div class="col-12 col-lg-12">
            <div class="card shadow-sm h-100 rounded-3">
              <div class="card-header bg-white border-bottom py-2 px-3">
                <div class="d-flex justify-content-between align-items-start gap-3">
                  <div>
                    <h2 class="h6 mb-0 d-flex align-items-center">
                      <mat-icon class="me-2 chevron-text-blue dashboard-icon-18">bar_chart</mat-icon>
                      Strategy Distribution
                    </h2>
                    <h3 class="mb-0 d-flex align-items-center">(Based on AI Analysis of CMDB Data)</h3>
                  </div>
                  @if (totalApplicationsKpi) {
                  <div class="strategy-box strategy-box--primary dashboard-cursor-pointer"
                    (click)="onKpiActionClick($event, totalApplicationsKpi, totalApplicationsKpi.assessmentStatus)"
                    tabindex="0">
                    <span class="strategy-box__label">{{ totalApplicationsKpi.title }}</span>
                    <strong class="strategy-box__count">{{ totalApplicationsKpi.value }}</strong>
                  </div>
                  }
                </div>
              </div>
              <div class="card-body p-3">
                <div class="strategy-boxes-grid mt-3">
                  @for (kpi of strategyKpis; track trackByKpiTitle(i, kpi); let i = $index) {
                  @if (kpi.assessmentStatus?.toLowerCase() !== 'decommission') {
                  <div class="strategy-box-card dashboard-cursor-pointer"
                    (click)="onKpiStrategyActionClick($event, kpi, kpi.assessmentStatus)" tabindex="0"
                    [matTooltip]="getStrategyOutcomeTooltip(kpi.title)"
                    [matTooltipDisabled]="!getStrategyOutcomeTooltip(kpi.title)"
                    matTooltipPosition="above">
                    <div class="strategy-box-card__content">
                      <div class="strategy-box-card__label">{{ kpi.title }}</div>
                      <div class="strategy-box-card__value" [style.color]="kpi.color">{{ kpi.displayValue }}</div>
                    </div>
                    <div class="strategy-box-card__icon" [style.color]="kpi.color">
                      <mat-icon>{{ kpi.icon }}</mat-icon>
                    </div>
                  </div>
                  }
                  }
                </div>
                <div class="progress mt-4 mt-2 dashboard-progress-lg">
                  @for (s of filteredStrategiesForChart; track trackByStrategyLabel(i, s); let i = $index) {
                  <div class="progress-bar" [style.background-color]="getStrategyColor(s.key)"
                    [style.width]="s.scaledPercent + '%'" [title]="s.label + ' — ' + s.scaledPercent + '%'"
                    role="progressbar" [attr.aria-valuenow]="s.scaledPercent" aria-valuemin="0" aria-valuemax="100">
                    @if (s.scaledPercent >= 1) {
                    <span class="fw-bold">{{ s.scaledPercent }}%</span>
                    }
                  </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Modernization Candidates -->
      <section class="candidates-section mb-3">
        @if (isLoadingTopApps) {
        <div class="row" style="margin-bottom: 20px;">
          @for (col of [1,2]; track col) {
          <div class="col-12 col-xl-6">
            <div class="card shadow-sm h-100 rounded-3">
              <div class="card-header candidates-card-header py-2 px-3" style="min-height:44px;display:flex;align-items:center;">
                <app-skeleton width="55%" height="14px" borderRadius="4px" marginBottom="0" class="skl-header-bar"></app-skeleton>
              </div>
              <div class="card-body p-0">
                <div class="skl-table-wrap">
                  <div class="skl-table-head-row">
                    <app-skeleton width="28%" height="12px" borderRadius="3px" marginBottom="0"></app-skeleton>
                    <app-skeleton width="28%" height="12px" borderRadius="3px" marginBottom="0"></app-skeleton>
                    <app-skeleton width="25%" height="12px" borderRadius="3px" marginBottom="0"></app-skeleton>
                  </div>
                  @for (row of [1,2,3,4,5]; track row) {
                  <div class="skl-table-row">
                    <div><app-skeleton width="85%" height="12px" borderRadius="3px" marginBottom="4px"></app-skeleton><app-skeleton width="55%" height="11px" borderRadius="3px" marginBottom="0"></app-skeleton></div>
                    <div><app-skeleton width="75%" height="12px" borderRadius="3px" marginBottom="4px"></app-skeleton><app-skeleton width="45%" height="11px" borderRadius="3px" marginBottom="0"></app-skeleton></div>
                    <div><app-skeleton width="65%" height="12px" borderRadius="3px" marginBottom="0"></app-skeleton></div>
                  </div>
                  }
                </div>
              </div>
              <div class="card-footer bg-light">
                <app-skeleton width="130px" height="13px" borderRadius="3px" marginBottom="12px"></app-skeleton>
                <div class="skl-donut-row">
                  <div class="skl-donut-stats">
                    @for (s of [1,2,3]; track s) {
                    <div class="skl-stat-row"><app-skeleton width="28px" height="16px" borderRadius="3px" marginBottom="0"></app-skeleton><app-skeleton width="44px" height="12px" borderRadius="3px" marginBottom="0"></app-skeleton></div>
                    }
                  </div>
                  <div class="skl-donut-circle-wrap"><div class="skl-donut-circle"></div></div>
                  <div class="skl-donut-legend">
                    @for (l of [1,2,3]; track l) {
                    <div class="skl-legend-row"><div class="skl-legend-dot"></div><app-skeleton width="40px" height="11px" borderRadius="3px" marginBottom="0"></app-skeleton><app-skeleton width="28px" height="11px" borderRadius="3px" marginBottom="0"></app-skeleton></div>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
          }
        </div>
        } @else {
        <div class="row" style="margin-bottom: 20px;">
          <!-- Top Modernization Candidates -->
          <div class="col-12 col-xl-6">
            <div class="card shadow-sm h-100 rounded-3">
              <div class="card-header candidates-card-header py-2 px-3 d-flex justify-content-between align-items-center">
                <h3 class="h6 mb-0 d-flex align-items-center candidates-card-title">
                  Top Modernization Candidates
                </h3>
              </div>
              <div class="card-body p-0">
                <div class="table-responsive dashboard-table-scroll">
                  <table class="table table-hover mb-0">
                    <thead class="table-light sticky-top">
                      <tr class="top-candidates-header">
                        <th>App Name</th>
                        <th>CMDB Score</th>
                        <th title="AI-generated lightweight source-code legacy score">Code Assessment Score</th>
                        <th>Code Assessment Rationale</th>
                        <th>Business Owner</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (app of topModernizationApps; track trackByRecentAppName(i, app); let i = $index) {
                      <tr class="dashboard-cursor-pointer" (click)="onCandidateClick(app)">
                        <td class="text-muted candidates-td"><span class="truncate-text" [title]="app.name">{{ app.name }}</span></td>
                        <td class="text-muted candidates-td"><span class="truncate-text" [title]="app.readinessScore?.toString() || '—'">{{ app.readinessScore ?? '—' }}</span></td>
                        <td class="text-muted candidates-td"><span class="truncate-text" [title]="app.lightweightCodeAssessmentScore?.toString() || '—'">{{ app.lightweightCodeAssessmentScore ?? '—' }}</span></td>
                        <td class="text-muted candidates-td"><span class="truncate-text" [title]="app.rationale || '—'">{{ app.rationale || '—' }}</span></td>
                        <td class="text-muted candidates-td"><span class="truncate-text" [title]="app.businessOwner || '—'">{{ app.businessOwner || '—' }}</span></td>
                      </tr>
                      }
                      @if (topModernizationApps.length === 0) {
                      <tr>
                        <td colspan="5" class="text-center text-muted py-3">No candidates found.</td>
                      </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="card-footer bg-light">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h3 class="h6 mb-0 d-flex align-items-center distribution-title">
                    Priority Distribution
                  </h3>
                </div>
                <div class="readiness-donut-footer">
                  <div class="readiness-stats">
                    <div class="mt-2">
                      <div class="readiness-count-row dashboard-cursor-pointer" (click)="onPriorityRowClick('modernize', 'high')" tabindex="0">
                        <strong class="readiness-count" style="color:#E5601F;">{{ modScores.high }}</strong>
                        <span class="readiness-count-label clickable-text">High</span>
                      </div>
                      <div class="readiness-count-row dashboard-cursor-pointer" (click)="onPriorityRowClick('modernize', 'medium')" tabindex="0">
                        <strong class="readiness-count" style="color:#009DD9;">{{ modScores.medium }}</strong>
                        <span class="readiness-count-label clickable-text">Medium</span>
                      </div>
                      <div class="readiness-count-row dashboard-cursor-pointer" (click)="onPriorityRowClick('modernize', 'low')" tabindex="0">
                        <strong class="readiness-count" style="color:#00B2BD;">{{ modScores.low }}</strong>
                        <span class="readiness-count-label clickable-text">Low</span>
                      </div>
                    </div>
                  </div>
                  <div class="readiness-donut-wrap">
                    <svg viewBox="0 0 120 120" class="readiness-donut-svg">
                      <circle cx="60" cy="60" r="45" fill="none" stroke="#e9ecef" stroke-width="18" />
                      @if (modScores.total > 0) {
                      <circle cx="60" cy="60" r="45" fill="none" stroke="#E5601F" stroke-width="18"
                        [attr.stroke-dasharray]="getDonutDash(modScores.high, modScores.total)"
                        [attr.stroke-dashoffset]="getDonutOffset(modScores.high, 0, modScores.total)"
                        transform="rotate(-90 60 60)" />
                      <circle cx="60" cy="60" r="45" fill="none" stroke="#009DD9" stroke-width="18"
                        [attr.stroke-dasharray]="getDonutDash(modScores.medium, modScores.total)"
                        [attr.stroke-dashoffset]="getDonutOffset(modScores.medium, modScores.high, modScores.total)"
                        transform="rotate(-90 60 60)" />
                      <circle cx="60" cy="60" r="45" fill="none" stroke="#00B2BD" stroke-width="18"
                        [attr.stroke-dasharray]="getDonutDash(modScores.low, modScores.total)"
                        [attr.stroke-dashoffset]="getDonutOffset(modScores.low, modScores.high + modScores.medium, modScores.total)"
                        transform="rotate(-90 60 60)" />
                      }
                      <text x="60" y="55" text-anchor="middle" class="donut-center-count">{{ modScores.total }}</text>
                      <text x="60" y="67" text-anchor="middle" class="donut-center-label">Modernization</text>
                      <text x="60" y="76" text-anchor="middle" class="donut-center-label">Candidates</text>
                    </svg>
                  </div>
                  <div class="readiness-legend">
                    <div class="readiness-legend-item">
                      <span class="readiness-legend-dot" style="background:#E5601F;"></span>
                      <span class="readiness-legend-name">High</span>
                      <span class="readiness-legend-pct">{{ modScores.total ? ((modScores.high / modScores.total * 100) |
                        number:'1.0-0') : 0 }}%</span>
                    </div>
                    <div class="readiness-legend-item">
                      <span class="readiness-legend-dot" style="background:#009DD9;"></span>
                      <span class="readiness-legend-name">Medium</span>
                      <span class="readiness-legend-pct">{{ modScores.total ? ((modScores.medium / modScores.total * 100)
                        |
                        number:'1.0-0') : 0 }}%</span>
                    </div>
                    <div class="readiness-legend-item">
                      <span class="readiness-legend-dot" style="background:#00B2BD;"></span>
                      <span class="readiness-legend-name">Low</span>
                      <span class="readiness-legend-pct">{{ modScores.total ? ((modScores.low / modScores.total * 100) |
                        number:'1.0-0') : 0 }}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Recent Code Assessments -->
          <div class="col-12 col-xl-6">
            <div class="card shadow-sm h-100 rounded-3">
              <div class="card-header candidates-card-header py-2 px-3 d-flex justify-content-between align-items-center">
                <h3 class="h6 mb-0 d-flex align-items-center candidates-card-title">
                  Recent Code Assessments
                </h3>
              </div>
              <div class="card-body p-0">
                @if (isLoadingRecentCodeAssessments) {
                <div class="skl-table-wrap">
                  <div class="skl-table-head-row">
                    <app-skeleton width="28%" height="12px" borderRadius="3px" marginBottom="0"></app-skeleton>
                    <app-skeleton width="28%" height="12px" borderRadius="3px" marginBottom="0"></app-skeleton>
                    <app-skeleton width="25%" height="12px" borderRadius="3px" marginBottom="0"></app-skeleton>
                  </div>
                  @for (row of [1,2,3,4,5]; track row) {
                  <div class="skl-table-row">
                    <div><app-skeleton width="85%" height="12px" borderRadius="3px" marginBottom="4px"></app-skeleton><app-skeleton width="55%" height="11px" borderRadius="3px" marginBottom="0"></app-skeleton></div>
                    <div><app-skeleton width="65%" height="12px" borderRadius="3px" marginBottom="0"></app-skeleton></div>
                    <div><app-skeleton width="75%" height="12px" borderRadius="3px" marginBottom="4px"></app-skeleton><app-skeleton width="45%" height="11px" borderRadius="3px" marginBottom="0"></app-skeleton></div>
                  </div>
                  }
                </div>
                } @else {
                <div class="table-responsive dashboard-table-scroll">
                  <table class="table table-hover mb-0">
                    <thead class="table-light sticky-top">
                      <tr class="recent-code-assessments-header">
                        <th>App Name</th>
                        <th>Status</th>
                        <th>Business Owner</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (item of recentCodeAssessments; track item.trackedBusinessApplicationId; let i = $index) {
                      <tr class="dashboard-cursor-pointer"
                        (click)="onViewCodeAssessmentDetails(item.trackedBusinessApplicationId)">
                        <td class="text-muted candidates-td"><span class="truncate-text" [title]="item.appName || '—'">{{ item.appName || '—' }}</span></td>
                        <td class="text-muted candidates-td"><span class="assessment-status" [title]="item.assessmentStatus || '—'">{{ item.assessmentStatus || '—'}}</span></td>
                        <td class="text-muted candidates-td"><span class="truncate-text" [title]="item.businessOwner || '—'">{{ item.businessOwner || '—' }}</span></td>
                      </tr>
                      }
                      @if (recentCodeAssessments.length === 0) {
                      <tr>
                        <td colspan="3" class="text-center text-muted py-3">No assessments found.</td>
                      </tr>
                      }
                    </tbody>
                  </table>
                </div>
                }
              </div>
              <div class="card-footer bg-light">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h3 class="h6 mb-0 d-flex align-items-center distribution-title">
                    Assessment Status Distribution
                  </h3>
                </div>
                <div class="readiness-donut-footer">
                  <div class="readiness-stats">
                    <div class="mt-2">
                      <div class="readiness-count-row">
                        <strong class="readiness-count" style="color:#28a745;">{{ completedAssessments }}</strong>
                        <span class="readiness-count-label clickable-text" (click)="onAssessmentClick($event, 'Completed')">Completed</span>
                      </div>
                      <div class="readiness-count-row">
                        <strong class="readiness-count" style="color:#dc3545;">{{ failedAssessments }}</strong>
                        <span class="readiness-count-label clickable-text" (click)="onAssessmentClick($event, 'Failed')">Failed</span>
                      </div>
                    </div>
                  </div>
                  <div class="readiness-donut-wrap">
                    <svg viewBox="0 0 120 120" class="readiness-donut-svg">
                      <circle cx="60" cy="60" r="45" fill="none" stroke="#e9ecef" stroke-width="18" />
                      @if ((completedAssessments + failedAssessments) > 0) {
                      <circle cx="60" cy="60" r="45" fill="none" stroke="#28a745" stroke-width="18"
                        [attr.stroke-dasharray]="getDonutDash(completedAssessments, completedAssessments + failedAssessments)"
                        [attr.stroke-dashoffset]="getDonutOffset(completedAssessments, 0, completedAssessments + failedAssessments)"
                        transform="rotate(-90 60 60)" />
                      <circle cx="60" cy="60" r="45" fill="none" stroke="#dc3545" stroke-width="18"
                        [attr.stroke-dasharray]="getDonutDash(failedAssessments, completedAssessments + failedAssessments)"
                        [attr.stroke-dashoffset]="getDonutOffset(failedAssessments, completedAssessments, completedAssessments + failedAssessments)"
                        transform="rotate(-90 60 60)" />
                      }
                      <text x="60" y="55" text-anchor="middle" class="donut-center-count">{{ completedAssessments +
                        failedAssessments }}</text>
                      <text x="60" y="67" text-anchor="middle" class="donut-center-label">Code</text>
                      <text x="60" y="76" text-anchor="middle" class="donut-center-label">Assessments</text>
                    </svg>
                  </div>
                  <div class="readiness-legend">
                    <div class="readiness-legend-item">
                      <span class="readiness-legend-dot" style="background:#28a745;"></span>
                      <span class="readiness-legend-name">Completed</span>
                      <span class="readiness-legend-pct">{{ (completedAssessments + failedAssessments) ?
                        ((completedAssessments / (completedAssessments + failedAssessments) * 100) | number:'1.0-0') : 0
                        }}%</span>
                    </div>
                    <div class="readiness-legend-item">
                      <span class="readiness-legend-dot" style="background:#dc3545;"></span>
                      <span class="readiness-legend-name">Failed</span>
                      <span class="readiness-legend-pct">{{ (completedAssessments + failedAssessments) ?
                        ((failedAssessments / (completedAssessments + failedAssessments) * 100) | number:'1.0-0') : 0
                        }}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        }
      </section>
    }
  </div>
  }

</main>
